import React, { Component } from "react";
import { inject, observer } from "mobx-react";
import { Button, Table, Loader } from "semantic-ui-react";
import { formatDateTimeFromApi, instantFromApi } from "../utils/dateTime";
import { Instant, ChronoUnit } from "js-joda";
import XTradesTable from "./XTradesTable";
import { formatCrypto, formatFiat, formatPercent2 } from "../utils/numberFormat";
import { sumXTradesTotal, revertIfShorting, getActualPotentialProfitOfTrade } from "../utils/botUtils";
import CurrencyPair from "../components/CurrencyPair";
import CandleSticksPanel from "../charts/CandleSticksPanel";
import LoadButtonPanel from "../components/LoadButtonPanel";
import DurationProgress from "../components/DurationProgress";
import SuccessRateIcon from "../bot/SuccessRateIcon";

const DEF_LIMIT = 25;

@inject("store")
@observer
class TradesTable extends Component {
  state = {
    expandedTrade: null,
    limit: DEF_LIMIT,
  };

  isRecent = apiValue => {
    return apiValue && instantFromApi(apiValue).isAfter(Instant.now().minus(1, ChronoUnit.HOURS));
  };

  componentWillReceiveProps(nextProps) {
    const { trades } = nextProps;
    if (
      this.state.expandedTrade &&
      (!trades || !trades.map(t => t.openOrderId).some(rowId => rowId === this.state.expandedTrade))
    ) {
      this.setState({ expandedTrade: null });
    }
  }

  render() {
    const {
      store: { zoneId },
      trades,
      botId,
      botConfig,
      closedTrades,
      exchangeType,
    } = this.props;
    if (!trades) {
      return <Loader inline active={this.props.loading} />;
    }

    const tradesRows = trades.filter((t, i) => i < this.state.limit).map((t, i) => {
      const profit = getActualPotentialProfitOfTrade(t);

      const closedState = t.state === "CLOSED";
      const closeTradeData = closedTrades ? closedTrades[i] : null;
      const currencyPair = t.currencyPair || this.props.botConfig.currencyPair;
      const baseCurrency = currencyPair.split("/")[0];
      const counterCurrency = currencyPair.split("/")[1];
      const shorting = t.shorting;
      const actualPrice = shorting ? t.currentMarketAsk : t.currentMarketBid;

      const openTotal = sumXTradesTotal(t.openXTradeList);
      const closeTotal = sumXTradesTotal(t.closeXTradeList);

      let priceDiff = null;
      let priceDiffCurrent = null;
      if (t.openPrice) {
        if (actualPrice) {
          priceDiffCurrent = actualPrice - t.openPrice;
        }

        if (t.closePrice) {
          priceDiff = t.closePrice - t.openPrice;
        } else if (priceDiffCurrent) {
          priceDiff = priceDiffCurrent;
        }
      }

      const expanded = t.openOrderId === this.state.expandedTrade;

      const pendingState = t.state.includes("PENDING_");
      const tradeColor = t.shorting ? "#9f3a38" : "#2c662d"; //red (SHORT) or green (LONG)

      const subRows = [
        <Table.Row
          key={"" + i}
          style={{
            opacity: this.state.expandedTrade && !expanded ? 0.3 : 1,
            transition: "opacity " + (this.state.expandedTrade && !expanded ? "2s" : "500ms"),
          }}
        >
          <Table.Cell warning={pendingState} style={{ position: "relative" }}>
            <Button
              size="mini"
              icon={"triangle " + (expanded ? "down" : "right")}
              onClick={() => this.setState({ expandedTrade: expanded ? null : t.openOrderId })}
              toggle
              active={expanded}
            />
            {t.state}
            {botConfig.forceSellAfterMinutes && (
              <DurationProgress
                config={botConfig}
                trade={t}
                closedTrade={closeTradeData}
                style={{
                  height: "5px",
                  opacity: "0.5",
                  position: "absolute",
                  bottom: "0px",
                  left: "0px",
                }}
              />
            )}
          </Table.Cell>

          <Table.Cell>
            <CurrencyPair exchangeType={exchangeType} currencyPair={currencyPair} />
            {!closedState && botId && <SuccessRateIcon key={botId + t.openOrderId} botId={botId} trade={t} />}
          </Table.Cell>

          <Table.Cell style={{ color: tradeColor }}>
            {t.shorting !== null ? (t.shorting ? "SHORT" : "LONG") : "SPOT"}
          </Table.Cell>

          <Table.Cell
            positive={
              pendingState
                ? revertIfShorting(shorting, priceDiffCurrent) > 0
                : revertIfShorting(shorting, priceDiff) > 0
            }
            negative={
              pendingState
                ? revertIfShorting(shorting, priceDiffCurrent) < 0
                : revertIfShorting(shorting, priceDiff) < 0
            }
          >
            {renderDiffPercent(priceDiff, t.openPrice, !t.closePrice)}
            {pendingState &&
              priceDiffCurrent &&
              priceDiffCurrent !== priceDiff &&
              " " + renderDiffPercent(priceDiffCurrent, t.openPrice, true)}
          </Table.Cell>
          <Table.Cell positive={profit > 0} negative={profit < 0}>
            {profit ? formatPercent2(profit / (t.leverage ? openTotal / t.leverage : openTotal)) : null}
          </Table.Cell>
          <Table.Cell positive={profit > 0} negative={profit < 0}>
            {profit ? formatFiat(profit) + " " + counterCurrency : null}
          </Table.Cell>

          <Table.Cell>
            <b>{formatCrypto(t.openPrice)}</b>
          </Table.Cell>
          <Table.Cell>{formatCrypto(t.openAmount)}</Table.Cell>
          <Table.Cell>{formatCrypto(t.leverage ? openTotal / t.leverage : openTotal)}</Table.Cell>
          <Table.Cell>{formatCrypto(openTotal)}</Table.Cell>
          <Table.Cell warning={this.isRecent(t.pendingOpenDate)}>
            {formatDateTimeFromApi(t.pendingOpenDate, zoneId)}
          </Table.Cell>

          <Table.Cell>{t.closeType}</Table.Cell>
          <Table.Cell>
            <b>{formatCrypto(t.closePrice)}</b>
          </Table.Cell>
          <Table.Cell>{formatCrypto(closeTotal)}</Table.Cell>
          <Table.Cell warning={this.isRecent(t.pendingCloseDate)}>
            {formatDateTimeFromApi(t.pendingCloseDate, zoneId)}
          </Table.Cell>
        </Table.Row>,
      ];

      if (expanded) {
        subRows.push(
          <Table.Row key={"" + i + ".xOpenTrades"}>
            <Table.Cell colSpan={11}>
              <XTradesTable
                xTrades={t.openXTradeList}
                open
                totalAmount={t.openAmount}
                counterCurrency={counterCurrency}
                headerText="Open xTrades"
              />
            </Table.Cell>
          </Table.Row>
        );
        subRows.push(
          <Table.Row key={"" + i + ".xCloseTrades"}>
            <Table.Cell colSpan={11}>
              <XTradesTable
                xTrades={t.closeXTradeList}
                totalAmount={t.openAmount}
                counterCurrency={counterCurrency}
                headerText="Close xTrades"
              />
            </Table.Cell>
          </Table.Row>
        );

        if (exchangeType) {
          subRows.push(
            <Table.Row key={"" + i + ".Chart"}>
              <Table.Cell colSpan={11}>
                <LoadButtonPanel
                  iconName="chart line"
                  loadText="Show stick chart"
                  createContentCb={() => {
                    const tradeData = closeTradeData
                      ? closeTradeData
                      : {
                          id: "OPEN_TRADE_ID", // A small hack: since there will be no more trades, we use just any id.
                          base: baseCurrency,
                          counter: counterCurrency,
                          openDate: t.pendingOpenDate,
                          openTotal: openTotal,
                          openFeeTotal: 0,
                          openAmount: t.openAmount,

                          closeDate: t.pendingCloseDate,
                          closeTotal: t.closeTotal,
                          closeFeeTotal: 0,
                          closeAmount: t.closeTotal,
                          profitTotal: 0,
                        };

                    return (
                      <CandleSticksPanel
                        singleTradeMode
                        config={botConfig}
                        currencyPairs={[currencyPair]}
                        exchangeType={exchangeType}
                        trades={[tradeData]}
                      />
                    );
                  }}
                />
              </Table.Cell>
            </Table.Row>
          );
        }
      }

      return subRows;
    });

    return (
      <div style={{ overflowX: "scroll" }}>
        {<Loader active={this.props.loading} />}
        <Table celled unstackable style={{ whiteSpace: "nowrap" }}>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>State</Table.HeaderCell>

              <Table.HeaderCell>Currency Pair</Table.HeaderCell>
              <Table.HeaderCell>Position Side</Table.HeaderCell>
              <Table.HeaderCell>Price Diff</Table.HeaderCell>
              <Table.HeaderCell>Profit %</Table.HeaderCell>
              <Table.HeaderCell>Profit {botConfig.currencyPair.split("/")[1]}</Table.HeaderCell>

              <Table.HeaderCell>Open Price</Table.HeaderCell>
              <Table.HeaderCell>Open Amount</Table.HeaderCell>
              <Table.HeaderCell>Used Money</Table.HeaderCell>
              <Table.HeaderCell>Open Total</Table.HeaderCell>
              <Table.HeaderCell>Pending Open Time</Table.HeaderCell>

              <Table.HeaderCell>Close Type</Table.HeaderCell>
              <Table.HeaderCell>Close Price</Table.HeaderCell>
              <Table.HeaderCell>Close Total</Table.HeaderCell>
              <Table.HeaderCell>Pending Close Time</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>{tradesRows}</Table.Body>
        </Table>
        {trades.length > this.state.limit && (
          <Button icon="angle down" onClick={() => this.setState({ limit: this.state.limit + DEF_LIMIT })} />
        )}
        {trades.length > this.state.limit && (
          <Button icon="angle double down" onClick={() => this.setState({ limit: Number.MAX_SAFE_INTEGER })} />
        )}
        {this.state.limit > DEF_LIMIT && (
          <Button icon="angle double up" onClick={() => this.setState({ limit: DEF_LIMIT })} />
        )}
      </div>
    );
  }
}

function renderDiffPercent(diff, total, temp) {
  if (diff) {
    const diffStr = formatPercent2(diff / total);
    if (temp) {
      return "(" + diffStr + ")";
    } else {
      return diffStr;
    }
  } else {
    return null;
  }
}

export default TradesTable;



// WEBPACK FOOTER //
// ./src/trade/TradesTable.js